import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { normalizeHtmlContent } from '@hunkware/shared/utils';

import { TextEditorFormProps, TextEditorFormValues } from '../types';
import { textEditorFormValidationSchema } from '../utils';

type UseTextEditorFormProps = Pick<
  TextEditorFormProps,
  'initialValues' | 'disabled' | 'onFormValuesChange' | 'onFormStateChange'
>;

export const useTextEditorForm = ({
  initialValues,
  disabled,
  onFormValuesChange,
  onFormStateChange,
}: UseTextEditorFormProps) => {
  const defaultValues = useMemo<TextEditorFormValues>(
    () => ({
      text: normalizeHtmlContent(initialValues?.text ?? ''),
    }),
    [initialValues],
  );

  const { control, formState, watch, reset, getValues, handleSubmit } = useForm<TextEditorFormValues>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(textEditorFormValidationSchema),
  });

  useEffect(() => {
    if (!disabled) {
      reset(defaultValues);
    }
  }, [disabled, defaultValues]);

  useEffect(() => {
    const subscription = watch(formValues => {
      onFormValuesChange?.(formValues as TextEditorFormValues);
    });
    return () => subscription.unsubscribe();
  }, [onFormValuesChange]);

  useEffect(() => {
    onFormStateChange?.(formState);
  }, [formState, formState.isValid, onFormStateChange]);

  return { control, getValues, handleSubmit };
};
